import { validateDraft } from "../validation";
import { memo, useEffect, useState } from "react";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  CoveredMatrixCallOption,
  CoveredMatrixMarketWatch,
  CoveredMatrixOrderPreviewResult,
  CreateCoveredMatrixOrderRequest,
} from "src/Interfaces/coveredMatrixOrder";
import {
  getCoveredMatrixLiveCriteria,
  getCoveredMatrixMarketWatch,
  previewCoveredMatrixOrder,
} from "src/Requests/coveredMatrixOrder";
import { TseIconLink } from "../Display";
import {
  tableRefreshInterval,
  maximumCostCriterion,
  formatCriterionValue,
} from "../model";
import { BaseInstrumentInfo } from "./BaseInstrumentInfo";

export const DraftOrderItemDetails = memo(
  ({
    draft,
    accounts,
    baseName,
    options,
  }: {
    draft: CreateCoveredMatrixOrderRequest;
    accounts: DropdownItem[];
    baseName: string;
    options: CoveredMatrixCallOption[];
  }) => {
    const [marketWatch, setMarketWatch] = useState<
      Record<string, CoveredMatrixMarketWatch>
    >({});
    const [liveCriteria, setLiveCriteria] = useState<
      Record<
        string,
        {
          gainAfterCommissionDaily: number | null;
          livePairPosition: number | null;
        }
      >
    >({});
    const [draftPreview, setDraftPreview] =
      useState<CoveredMatrixOrderPreviewResult | null>(null);
    const normalizeInstrumentId = (value: string) => value.trim().toUpperCase();
    const getByInstrumentId = <T,>(
      values: Record<string, T>,
      instrumentId: string,
    ): T | undefined => {
      const normalizedId = normalizeInstrumentId(instrumentId);
      return Object.entries(values).find(
        ([key]) => normalizeInstrumentId(key) === normalizedId,
      )?.[1];
    };
    const instrumentIdsKey = [
      draft.baseIsin,
      ...draft.items.map((item) => item.optionIsin),
    ].join("|");
    const liveCriteriaKey = JSON.stringify(
      draft.items.map(({ optionIsin, criterionType }) => ({
        optionIsin,
        criterionType,
      })),
    );
    useEffect(() => {
      const instrumentIds = instrumentIdsKey.split("|").filter(Boolean);
      if (!instrumentIds.length) {
        setMarketWatch({});
        return;
      }
      let disposed = false;
      const load = () =>
        getCoveredMatrixMarketWatch(instrumentIds)
          .then((result) => {
            if (!disposed) setMarketWatch(result);
          })
          .catch(() => {
            if (!disposed) setMarketWatch({});
          });
      load();
      const timer = window.setInterval(load, tableRefreshInterval);
      return () => {
        disposed = true;
        window.clearInterval(timer);
      };
    }, [instrumentIdsKey]);
    useEffect(() => {
      const items: Array<{ optionIsin: string; criterionType: number }> =
        JSON.parse(liveCriteriaKey);
      if (!draft.baseIsin || !items.length) {
        setLiveCriteria({});
        return;
      }
      let disposed = false;
      const load = () =>
        getCoveredMatrixLiveCriteria({
          baseIsin: draft.baseIsin,
          executionMethod: draft.executionMethod,
          manualBaseReferencePrice: draft.manualBaseReferencePrice,
          items,
        })
          .then((result) => {
            if (disposed) return;
            setLiveCriteria(
              Object.fromEntries(
                result.map((item) => [
                  normalizeInstrumentId(item.optionIsin),
                  {
                    gainAfterCommissionDaily: item.gainAfterCommissionDaily,
                    livePairPosition: item.livePairPosition,
                  },
                ]),
              ),
            );
          })
          .catch(() => {
            if (!disposed) setLiveCriteria({});
          });
      load();
      const timer = window.setInterval(load, tableRefreshInterval);
      return () => {
        disposed = true;
        window.clearInterval(timer);
      };
    }, [
      draft.baseIsin,
      draft.executionMethod,
      draft.manualBaseReferencePrice,
      liveCriteriaKey,
    ]);
    useEffect(() => {
      setDraftPreview(null);
      const canPreview = validateDraft(draft) === null;
      if (!canPreview) {
        setDraftPreview(null);
        return;
      }
      let disposed = false;
      const timer = window.setTimeout(() => {
        previewCoveredMatrixOrder(draft)
          .then((result) => {
            if (!disposed) setDraftPreview(result);
          })
          .catch(() => {
            if (!disposed) setDraftPreview(null);
          });
      }, 500);
      return () => {
        disposed = true;
        window.clearTimeout(timer);
      };
    }, [draft]);
    const format = (value: number | string | null | undefined) =>
      value === null || value === undefined || value === ""
        ? "-"
        : typeof value === "number"
          ? value.toLocaleString()
          : value.toString();
    const formatPreviewValues = (
      selector: (
        row: CoveredMatrixOrderPreviewResult["rows"][number],
      ) => number,
    ) => {
      const rows = draftPreview?.rows || [];
      if (!rows.length) return "-";
      const values = Array.from(new Set(rows.map(selector)));
      if (values.length === 1) return format(values[0]);
      return rows
        .map((row) => `${row.accountName}: ${format(selector(row))}`)
        .join(" | ");
    };
    const selectedAccountNames = draft.accountIds.map(
      (id) =>
        accounts.find((account) => Number(account.id) === id)?.name ||
        `اکانت ${id}`,
    );
    const accountLabel = selectedAccountNames.length
      ? selectedAccountNames.join("، ")
      : "-";
    const baseMarketWatch = getByInstrumentId(marketWatch, draft.baseIsin);
    const hasWideBaseVolume = [
      baseMarketWatch?.buyVolume,
      baseMarketWatch?.sellVolume,
    ].some(
      (value) =>
        value !== null &&
        value !== undefined &&
        Math.abs(Number(value)) >= 1_000_000,
    );
    const baseReferencePrice =
      draft.manualBaseReferencePrice ?? baseMarketWatch?.lastPrice ?? null;
    const optionName = (id: string) =>
      options.find((option) => option.id.toString() === id)?.name || id;
    const targetVolume = formatPreviewValues((row) => row.targetVolume);
    const headers = [
      "#",
      "نماد\nآپشن",
      "حجم",
      "خرید",
      "فروش",
      "حجم",
      "% بازده\nروزانه",
      "ب ت\nلحظه‌ای",
      "حجم\nباز",
      "حجم\nهدف",
      "حجم\nانجام\n(معادل دارایی پایه)",
      "قیمت انجام\nقبل از کارمزد",
      "قیمت انجام\nبعد از کارمزد",
      "معیار هدف",
    ];
    const rows = draft.items.map((item, rowIndex) => {
      const market = getByInstrumentId(marketWatch, item.optionIsin);
      const live = liveCriteria[normalizeInstrumentId(item.optionIsin)];
      return {
        id: rowIndex + 1,
        row: rowIndex + 1,
        option: {
          text: optionName(item.optionIsin),
          href: market?.tseLink || "",
          icon: "tsetmc",
          title: "نمایش اردربوک در TSETMC",
        },
        buyVolume: format(market?.buyVolume),
        buyPrice: format(market?.buyPrice),
        sellPrice: format(market?.sellPrice),
        sellVolume: format(market?.sellVolume),
        dailyReturn: formatCriterionValue(live?.gainAfterCommissionDaily),
        cost: formatCriterionValue(
          live?.livePairPosition,
          maximumCostCriterion,
        ),
        openVolume: "-",
        targetVolume,
        doneVolume: "-",
        beforeCommission: "-",
        afterCommission: "-",
        criterion: formatCriterionValue(
          item.criterionValue,
          item.criterionType,
        ),
        buyVolumeClassname:
          market?.buyOrderBookIsValid === false ? "text-gray-300" : "",
        buyPriceClassname:
          market?.buyOrderBookIsValid === false ? "text-gray-300" : "",
        sellVolumeClassname:
          market?.sellOrderBookIsValid === false ? "text-gray-300" : "",
        sellPriceClassname:
          market?.sellOrderBookIsValid === false ? "text-gray-300" : "",
      };
    });
    const keys = [
      "row",
      "option",
      "buyVolume",
      "buyPrice",
      "sellPrice",
      "sellVolume",
      "dailyReturn",
      "cost",
      "openVolume",
      "targetVolume",
      "doneVolume",
      "beforeCommission",
      "afterCommission",
      "criterion",
    ] as const;
    const widths = [32, 110, 58, 58, 58, 58, 72, 78, 60, 72, 118, 100, 100, 72];
    const cellColor = (index: number, header = false) => {
      if (index === 2 || index === 3)
        return header ? "bg-[#a8dfbd] text-green-900" : "bg-[#eaf8ef]";
      if (index === 4 || index === 5)
        return header ? "bg-[#f8c5d2] text-slate-900" : "bg-[#fde9ed]";
      return "";
    };
    return (
      <div
        className={`grid min-w-0 grid-cols-1 items-start gap-2 xl:items-stretch ${hasWideBaseVolume ? "xl:grid-cols-[340px_minmax(0,1fr)]" : "xl:grid-cols-[270px_minmax(0,1fr)]"}`}
        dir="rtl"
      >
        <BaseInstrumentInfo
          name={baseName}
          marketWatch={baseMarketWatch}
          referencePrice={baseReferencePrice}
          account={accountLabel}
          holding={formatPreviewValues((row) => row.baseHoldingVolume)}
          imbalance={formatPreviewValues(
            (row) => row.portfolioCoveredCallImbalance,
          )}
        />
        <div className="relative min-h-0 min-w-0 w-full self-start xl:self-stretch">
          <div className="max-h-[226px] min-w-0 w-full overflow-auto rounded border  xl:absolute xl:inset-0 xl:max-h-none">
            <table className="w-full min-w-[1140px] table-fixed border-collapse text-xs">
              <colgroup>
                {widths.map((width, index) => (
                  <col key={index} style={{ width }} />
                ))}
              </colgroup>
              <thead className="sticky top-0 z-20 bg-white">
                <tr>
                  {headers.map((header, index) => (
                    <th
                      key={index}
                      className={`whitespace-pre-line border border-slate-300 px-1 py-1 leading-tight ${cellColor(index, true)}`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={rows.length ? undefined : "!border-0"}>
                {rows.length ? (
                  rows.map((row) => (
                    <tr key={row.id} className="h-8">
                      {keys.map((key, index) => {
                        const value = row[key];
                        const validity =
                          key === "buyVolume" || key === "buyPrice"
                            ? row.buyPriceClassname
                            : key === "sellPrice" || key === "sellVolume"
                              ? row.sellPriceClassname
                              : "";
                        return (
                          <td
                            key={key}
                            className={`overflow-hidden text-ellipsis whitespace-nowrap border border-slate-300 px-1 py-1 text-center tabular-nums ${cellColor(index)} ${validity}`}
                            title={
                              typeof value === "string" ? value : undefined
                            }
                          >
                            {key === "option" ? (
                              <span className="inline-flex items-center gap-1">
                                <span className="max-w-[84px] truncate">
                                  {row.option.text}
                                </span>
                                <TseIconLink href={row.option.href} />
                              </span>
                            ) : typeof value === "object" ? null : (
                              value
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr className="!border-0">
                    <td
                      colSpan={headers.length}
                      className="!border-0 py-8 text-center text-gray-400"
                    >
                      نماد سمت دوم را انتخاب کنید.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  },
);
